'use client';

import React, { useEffect, useState } from 'react';
import { Bot, X } from 'lucide-react';
import { trackChatbotEvent } from './chatbot-tracking';
import { CHATBOT_NAME } from './chatbot-identity';
import { CHATBOT_HEADER_GRADIENT } from './chatbot-theme';

const AUTO_DISMISS_MS = 10_000;

export const NavigationToast: React.FC<{
  label: string;
  url: string;
  onDismiss: () => void;
  onNotUseful: () => void;
}> = ({ label, url, onDismiss, onNotUseful }) => {
  const [visible, setVisible] = useState(false);

  // Animate in on mount
  useEffect(() => {
    const raf = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  // Auto-dismiss after timeout
  useEffect(() => {
    const timer = setTimeout(() => {
      setVisible(false);
      setTimeout(onDismiss, 200); // Wait for fade-out before removing
    }, AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [onDismiss]);

  const handleDismiss = () => {
    trackChatbotEvent('Chatbot Navigation Feedback', { url, feedback: 'helpful' });
    setVisible(false);
    setTimeout(onDismiss, 200);
  };

  const handleNotUseful = () => {
    trackChatbotEvent('Chatbot Navigation Feedback', { url, feedback: 'not_helpful' });
    setVisible(false);
    setTimeout(onNotUseful, 200);
  };

  return (
    <div
      data-page-overlay="chatbot"
      className={`fixed bottom-4 right-4 z-50 w-[22rem] sm:w-96 rounded-box shadow-2xl bg-base-100 border border-base-300 overflow-hidden transition-all duration-200 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
      }`}
    >
      {/* Header bar */}
      <div
        className="flex items-center justify-between px-4 py-2.5 text-primary-content"
        style={{ backgroundImage: CHATBOT_HEADER_GRADIENT }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <div className="avatar avatar-placeholder">
            <div className="w-6 rounded-full bg-primary-content/15 text-primary-content">
              <Bot className="size-4" />
            </div>
          </div>
          <span className="text-sm font-semibold">{CHATBOT_NAME}</span>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          className="btn btn-ghost btn-xs btn-square text-primary-content/90 hover:bg-primary-content/15"
        >
          <X className="size-3.5" />
          <span className="sr-only">Dismiss</span>
        </button>
      </div>

      {/* Body */}
      <div className="px-4 py-3">
        <p className="text-sm text-base-content">
          I&apos;ve taken you to <span className="font-medium">{label}</span>.
        </p>

        <div className="flex gap-2 mt-3">
          <button
            type="button"
            onClick={handleNotUseful}
            className="btn btn-sm btn-outline flex-1"
          >
            Not what I wanted
          </button>
          <button
            type="button"
            onClick={handleDismiss}
            className="btn btn-sm btn-primary flex-1"
          >
            Thanks!
          </button>
        </div>
      </div>
    </div>
  );
};
